// QR Billing API Service
// Centralized API calls for QR Billing module

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, ''); // Remove trailing slashes

// Helper to get auth headers
const getHeaders = () => {
  const token = localStorage.getItem('token');
  const businessId = localStorage.getItem('businessId');
  
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...(businessId && { 'X-Business-Id': businessId })
  };
};

// Dashboard APIs
export const qrBillingService = {
  // Get complete dashboard with all tables
  getDashboard: async (floor = null) => {
    const params = new URLSearchParams();
    if (floor) params.append('floor', floor);
    
    // 🔥 Add timestamp to prevent caching issues after table configuration changes
    params.append('_t', Date.now());
    
    const url = `${API_BASE_URL}/qr-billing/dashboard?${params.toString()}`;
    
    
    const response = await fetch(url, {
      headers: getHeaders(),
      cache: 'no-store' // Disable browser caching
    });
    
    if (!response.ok) {
      throw new Error(`Dashboard fetch failed: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Get detailed orders for a specific table
  getTableDetails: async (tableNumber) => {
    const response = await fetch(`${API_BASE_URL}/qr-billing/tables/${tableNumber}/details`, {
      headers: getHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Table details fetch failed: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Get order history (all or filtered by table)
  getOrderHistory: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.tableNumber) params.append('tableNumber', filters.tableNumber);
    if (filters.date) params.append('date', filters.date);
    
    const url = `${API_BASE_URL}/qr-billing/orders/history${params.toString() ? '?' + params.toString() : ''}`;
    
    const response = await fetch(url, {
      headers: getHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Order history fetch failed: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Menu Management APIs
  getMenu: async () => {
    const response = await fetch(`${API_BASE_URL}/qr-billing/menu`, {
      headers: getHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Menu fetch failed: ${response.statusText}`);
    }
    
    return response.json();
  },

  updateMenuItem: async (itemId, updates) => {
    const response = await fetch(`${API_BASE_URL}/qr-billing/menu/${itemId}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    
    if (!response.ok) {
      throw new Error(`Menu item update failed: ${response.statusText}`);
    }
    
    return response.json();
  },

  toggleItemAvailability: async (itemId, isAvailable) => {
    const response = await fetch(`${API_BASE_URL}/qr-billing/menu/${itemId}/availability`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ isAvailable })
    });
    
    if (!response.ok) {
      throw new Error(`Availability toggle failed: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Analytics APIs
  getAnalytics: async () => {
    const response = await fetch(`${API_BASE_URL}/qr-billing/analytics`, {
      headers: getHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Analytics fetch failed: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Configuration Management APIs
  saveConfiguration: async (configData) => {
    const response = await fetch(`${API_BASE_URL}/qr-billing/configuration`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(configData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Configuration save failed: ${response.statusText}`);
    }
    
    return response.json();
  },

  getConfiguration: async () => {
    const response = await fetch(`${API_BASE_URL}/qr-billing/configuration`, {
      headers: getHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Configuration fetch failed: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Partial Progress Management APIs
  savePartialProgress: async (progressData) => {
    const response = await fetch(`${API_BASE_URL}/qr-billing/save-partial-progress`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(progressData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Partial progress save failed: ${response.statusText}`);
    }
    
    return response.json();
  },

  clearPartialProgress: async () => {
    const response = await fetch(`${API_BASE_URL}/qr-billing/clear-partial-progress`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Clear partial progress failed: ${response.statusText}`);
    }
    
    return response.json();
  }
};

export default qrBillingService;
